import { apiFetch } from "./client";
import type { ErpGroupOption } from "./leads";

export type CompanyStatus = "ACTIVE" | "SUSPENDED" | "DELETED";
export type UserStatus = "ACTIVE" | "LOCKED" | "DISABLED";
/** CUSTOMER / SUPPLIER / BOTH; null for admin-created companies and the internal firm. */
export type CompanyKind = "CUSTOMER" | "SUPPLIER" | "BOTH";

export type PriceList = { id: string; code: string; name: string; currency: string; isDefault: boolean };

export type Company = {
  id: string;
  name: string;
  vatNumber: string | null;
  /** For a branch: the VAT number of its parent company, which the branch shares. */
  parentVatNumber: string | null;
  erpId: string | null;
  country: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  defaultLocale: string;
  status: CompanyStatus;
  /** CUSTOMER / SUPPLIER / BOTH; null for admin-created companies and the internal firm. */
  kind: CompanyKind | null;
  /** True when the company has ≥1 active, non-deleted portal user (an onboarded "Kupac"). */
  registered: boolean;
  otkupAccountId: string | null;
  otkupAccountName: string | null;
  priceListId?: string | null;
  priceListName?: string | null;
  displayDiscount: boolean;
  forecastsEnabled: boolean;
  /** Otkup: who issues the document for a purchase. */
  settlementMode: "SELF_BILLED" | "SUPPLIER_INVOICES";
  /** Otkup: days from a self-billed settlement to its due date. Null = not agreed (F.2). */
  paymentTermDays: number | null;
  /** Comma-separated product lines this customer buys, from the CRM import. */
  tags: string | null;
  /** The company's contact person (primary first) — most customers have one of
   * these and no portal login at all. */
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CompanyCreateBody = {
  name: string;
  vatNumber?: string;
  erpId?: string;
  country?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postalCode?: string;
  defaultLocale?: string;
  status?: CompanyStatus;
  displayDiscount?: boolean;
  forecastsEnabled?: boolean;
  assignedSalesUserId?: string;
  otkupAccountId?: string;
  priceListId?: string;
  /** Tip firme: CUSTOMER / SUPPLIER / BOTH. Omitted → server defaults to CUSTOMER on create. */
  kind?: CompanyKind | null;
  /** A new customer goes to the ERP, which needs these too (see ErpCustomerFields). */
  registrationNumber?: string;
  erpGroup?: string;
};

/** One company contact — a person who matters for correspondence. `linkedUserId`
 * non-null when the contact is also a portal member (drives the badge). */
export type CompanyContact = {
  id: string;
  companyId: string;
  name: string;
  position: string | null;
  email: string | null;
  phone: string | null;
  primary: boolean;
  note: string | null;
  linkedUserId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CompanyContactBody = {
  name: string;
  position?: string | null;
  email?: string | null;
  phone?: string | null;
  primary?: boolean;
  note?: string | null;
};

export type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  defaultLocale: string;
  erpId: string | null;
  /** ERP sector the user's documents are filed under in the ERP. */
  erpSector: number | null;
  phone: string | null;
  avatarUrl: string | null;
  status: UserStatus;
  companyId: string | null;
  companyName: string | null;
  internal: boolean;
  maxDiscountPct: number | null;
  roles: string[];
  lastLoginAt: string | null;
  createdAt: string;
};

/** One entry in a company's address book (delivery or billing). */
export type CompanyAddress = {
  id: string;
  companyId: string;
  label: string | null;
  recipientName: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  vatNumber: string | null;
  usableForDelivery: boolean;
  usableForBilling: boolean;
  defaultDelivery: boolean;
  defaultBilling: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

/** Create/update body for an address-book entry. The `default*` flags are only
 * honoured on the internal management path; the customer inline-create derives
 * defaults server-side. */
export type CompanyAddressBody = {
  label?: string | null;
  recipientName?: string | null;
  addressLine1: string;
  addressLine2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  vatNumber?: string | null;
  usableForDelivery?: boolean;
  usableForBilling?: boolean;
  defaultDelivery?: boolean;
  defaultBilling?: boolean;
  active?: boolean;
};

export type AddressPurpose = "delivery" | "billing";

export type SalesAssignment = {
  id: string;
  companyId: string;
  salesUserId: string;
  salesUserEmail: string;
  salesUserFullName: string;
  primary: boolean;
  assignedAt: string;
};

export type SalesCoverageEntry = {
  id: string | null;           // null for SCOPE entries
  userId: string;
  name: string;
  email: string;
  primary: boolean;
  assignedAt: string | null;
  origin: "DIRECT" | "SCOPE";
  countryCode: string | null;  // populated for SCOPE entries
};

export type SalesCoverage = {
  direct: SalesCoverageEntry[];
  viaScope: SalesCoverageEntry[];
};

/** One OTKUP referent assigned to a supplier company. The assignment is what
 * lets the referent act on the company (announce, contract, settle); every
 * referent can read every supplier regardless. */
export type OtkupAssignment = {
  id: string;
  companyId: string;
  otkupUserId: string;
  otkupUserEmail: string;
  otkupUserFullName: string;
  primary: boolean;
  assignedAt: string;
};

/** Partial update: a field left out stays as it is. Blanking the payment term
 * or the otkup rep goes through the `clear*` flags, since an absent field
 * means "unchanged". */
export type CompanyUpdateBody = Partial<Company> & {
  clearPaymentTermDays?: boolean;
  clearOtkupAccountId?: boolean;
};

export type Invitation = {
  id: string;
  email: string;
  companyId: string | null;
  companyName: string | null;
  roles: string[];
  invitedBy: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
};

export type Page<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

/** Jedna ponuđena vrednost filtera i koliko firmi je nosi. */
export type FilterOption = { value: string; count: number };
/** Opcija čija vrednost sama po sebi ništa ne kaže, pa uz nju ide i natpis — npr. id referenta. */
export type ImenovanaOpcija = FilterOption & { label: string };

const buildPageQuery = (page: number, size: number) =>
  `?page=${page}&size=${size}&sort=createdAt,desc`;

export const adminApi = {
  // Companies
  /** Filter and search run in the database: thirteen thousand companies never fit one page. */
  listCompanies: (
    page = 0,
    size = 20,
    kind?: string,
    q?: string,
    country?: string,
    line?: string,
    otkupAccountId?: string,
  ) => {
    const extra = [
      kind ? `kind=${encodeURIComponent(kind)}` : "",
      country ? `country=${encodeURIComponent(country)}` : "",
      line ? `line=${encodeURIComponent(line)}` : "",
      otkupAccountId ? `otkupAccountId=${encodeURIComponent(otkupAccountId)}` : "",
      q && q.trim() ? `q=${encodeURIComponent(q.trim())}` : "",
    ].filter(Boolean).join("&");
    const base = buildPageQuery(page, size);
    return apiFetch<Page<Company>>(`/admin/companies${base}${extra ? `&${extra}` : ""}`);
  },
  /** Vrednosti koje padajući spiskovi filtera nude — samo ono što u bazi stvarno postoji. */
  companyFilters: () =>
    apiFetch<{ countries: FilterOption[]; lines: FilterOption[]; otkupOfficers: ImenovanaOpcija[] }>(
      "/admin/companies/filters",
    ),
  /** Tab counts from the server, over the same visible set as the list. */
  companyCounts: () =>
    apiFetch<{ customers: number; suppliers: number; all: number }>("/admin/companies/counts"),
  getCompany: (id: string) => apiFetch<Company>(`/admin/companies/${id}`),
  createCompany: (body: CompanyCreateBody) =>
    apiFetch<Company>("/admin/companies", { method: "POST", body }),
  /** The ERP's customer groups in the order of its tree, each with its path there: the kind of a new customer. */
  erpGroups: () => apiFetch<ErpGroupOption[]>("/admin/companies/erp-groups"),
  updateCompany: (id: string, body: CompanyUpdateBody) =>
    apiFetch<Company>(`/admin/companies/${id}`, { method: "PUT", body }),
  deleteCompany: (id: string) =>
    apiFetch<void>(`/admin/companies/${id}`, { method: "DELETE" }),
  listCompanySales: (id: string) =>
    apiFetch<SalesAssignment[]>(`/admin/companies/${id}/sales-assignments`),
  listCompanySalesCoverage: (id: string) =>
    apiFetch<SalesCoverage>(`/admin/companies/${id}/sales-coverage`),
  assignSales: (id: string, body: { salesUserId: string; primary: boolean }) =>
    apiFetch<SalesAssignment>(`/admin/companies/${id}/sales-assignments`, {
      method: "POST",
      body,
    }),
  removeSales: (companyId: string, assignmentId: string) =>
    apiFetch<void>(
      `/admin/companies/${companyId}/sales-assignments/${assignmentId}`,
      { method: "DELETE" },
    ),
  listCompanyOtkup: (id: string) =>
    apiFetch<OtkupAssignment[]>(`/admin/companies/${id}/otkup-assignments`),
  assignOtkup: (id: string, body: { otkupUserId: string; primary: boolean }) =>
    apiFetch<OtkupAssignment>(`/admin/companies/${id}/otkup-assignments`, {
      method: "POST",
      body,
    }),
  removeOtkup: (companyId: string, otkupUserId: string) =>
    apiFetch<void>(
      `/admin/companies/${companyId}/otkup-assignments/${otkupUserId}`,
      { method: "DELETE" },
    ),
  listPriceLists: () => apiFetch<PriceList[]>("/admin/price-lists"),

  // Company address book (internal management, companies:edit)
  listCompanyAddresses: (companyId: string) =>
    apiFetch<CompanyAddress[]>(`/admin/companies/${companyId}/addresses`),
  createCompanyAddress: (companyId: string, body: CompanyAddressBody) =>
    apiFetch<CompanyAddress>(`/admin/companies/${companyId}/addresses`, {
      method: "POST",
      body,
    }),
  updateCompanyAddress: (companyId: string, addressId: string, body: CompanyAddressBody) =>
    apiFetch<CompanyAddress>(`/admin/companies/${companyId}/addresses/${addressId}`, {
      method: "PUT",
      body,
    }),
  deleteCompanyAddress: (companyId: string, addressId: string) =>
    apiFetch<void>(`/admin/companies/${companyId}/addresses/${addressId}`, {
      method: "DELETE",
    }),
  listCompanyContacts: (companyId: string) =>
    apiFetch<CompanyContact[]>(`/admin/companies/${companyId}/contacts`),
  createCompanyContact: (companyId: string, body: CompanyContactBody) =>
    apiFetch<CompanyContact>(`/admin/companies/${companyId}/contacts`, {
      method: "POST",
      body,
    }),
  updateCompanyContact: (companyId: string, contactId: string, body: CompanyContactBody) =>
    apiFetch<CompanyContact>(`/admin/companies/${companyId}/contacts/${contactId}`, {
      method: "PUT",
      body,
    }),
  deleteCompanyContact: (companyId: string, contactId: string) =>
    apiFetch<void>(`/admin/companies/${companyId}/contacts/${contactId}`, {
      method: "DELETE",
    }),
  setCompanyAddressDefault: (companyId: string, addressId: string, purpose: AddressPurpose) =>
    apiFetch<CompanyAddress>(
      `/admin/companies/${companyId}/addresses/${addressId}/set-default?purpose=${purpose}`,
      { method: "PATCH" },
    ),

  // Users
  listUsers: (page = 0, size = 20) =>
    apiFetch<Page<User>>(`/admin/users${buildPageQuery(page, size)}`),
  getUser: (id: string) => apiFetch<User>(`/admin/users/${id}`),
  updateUser: (id: string, body: Partial<User>) =>
    apiFetch<User>(`/admin/users/${id}`, { method: "PUT", body }),
  enableUser: (id: string) =>
    apiFetch<User>(`/admin/users/${id}/enable`, { method: "POST" }),
  disableUser: (id: string) =>
    apiFetch<User>(`/admin/users/${id}/disable`, { method: "POST" }),
  deleteUser: (id: string) =>
    apiFetch<void>(`/admin/users/${id}`, { method: "DELETE" }),

  // Invitations
  listPendingInvitations: () => apiFetch<Invitation[]>("/admin/invitations"),
  createInvitation: (body: {
    email: string;
    companyId?: string;
    roles: string[];
    locale?: string;
    erpId?: string;
    maxDiscountPct?: number;
  }) => apiFetch<Invitation>("/admin/invitations", { method: "POST", body }),
  resendInvitation: (id: string) =>
    apiFetch<Invitation>(`/admin/invitations/${id}/resend`, { method: "POST" }),
  revokeInvitation: (id: string) =>
    apiFetch<void>(`/admin/invitations/${id}`, { method: "DELETE" }),
};
